import { Controller } from '@hotwired/stimulus'
import { useClickOutside } from 'stimulus-use'
import { get } from '@rails/request.js'
import hotkeys from 'hotkeys-js'

export default class extends Controller {
  static targets = [
    'listbox',
    'listboxContainer',
    'loadingContainer',
    'option',
    'optionCheck',
    'resultsContainer',
    'searchInput',
    'toggle',
  ]

  static values = {
    cabinetId: String,
    disabled: { default: true, type: Boolean },
    id: String,
    url: String,
  }

  connect() {
    this.hotkeysScope = 'global-drawers-combobox'

    useClickOutside(this)

    hotkeys(
      'escape',
      { scope: this.hotkeysScope },
      () => { this.hide() },
    )

    hotkeys.filter = (event) => {
      const { tagName } = event.target

      return (event.target.classList.contains('js-hotkeys-enabled')) ||
        !(/^(INPUT|TEXTAREA|SELECT)/.test(tagName))
    }
  }

  cabinetIdValueChanged() {
    this.disabledValue = (this.cabinetIdValue === '')
    this.#storeSelectedOption({ label: '', value: '' })
    this.dispatch(
      'selectionChange',
      { detail: { drawerId: '' } },
    )
  }

  clickOutside() {
    this.hide()
  }

  delayedFilter() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => { this.#filter() }, 250)
  }

  disabledValueChanged() {
    this.searchInputTarget.disabled = this.disabledValue
    this.toggleTarget.disabled = this.disabledValue
  }

  handleCabinetChange({ detail: { cabinetId } }) {
    this.cabinetIdValue = cabinetId
  }

  handleUploadStateChange({ detail: { disableInput } }) {
    this.disabledValue = disableInput
  }

  hide() {
    if (hotkeys.getScope() === this.hotkeysScope) {
      hotkeys.setScope(this.previousScope)
    }

    this.#resetInputValueIfPresent()

    this.searchInputTarget.setAttribute('aria-expanded', false)
    this.resultsContainerTarget.classList.add('hidden')
  }

  initialFilter() {
    if (!this.disabledValue) {
      if (hotkeys.getScope() !== this.hotkeysScope) {
        this.previousScope = hotkeys.getScope()
      }
      hotkeys.setScope(this.hotkeysScope)

      this.resultsContainerTarget.classList.remove('hidden')
      this.#filter()
    }
  }

  select(event) {
    this.#applySelectedOptionStyling(event.target)
    this.#storeSelectedOption({
      label: event.target.dataset.searchContext,
      value: event.target.dataset.selectValue,
    })

    this.dispatch(
      'selectionChange',
      { detail: { drawerId: event.target.dataset.selectValue } },
    )

    this.hide()
  }

  toggle() {
    if (this.resultsContainerTarget.classList.contains('hidden')) {
      if (!this.disabledValue) this.#focusInputTarget()
    } else {
      this.hide()
    }
  }

  #applySelectedOptionStyling(option) {
    const optionCheck = option.querySelector(
      "[data-clients--global-drawer-combobox-target='optionCheck']",
    )

    option.classList.add('font-semibold')
    optionCheck.classList.remove('hidden')
  }

  async #filter() {
    this.#showLoadingContainer()

    const response = await get(
      this.urlValue,
      {
        query: {
          cabinet_id: this.cabinetIdValue,
          term: this.searchInputTarget.value,
        },
        responseKind: 'json',
      },
    )

    if (!response.ok) {
      throw new Error(response.status)
    }

    const data = await response.json
    this.listboxContainerTarget.innerHTML = data.results
    this.#showListboxContainer()
    this.#styleSelectedOption()
  }

  #focusInputTarget() {
    this.searchInputTarget.focus()
    const resetValueToMoveCursor = this.searchInputTarget.value
    this.searchInputTarget.value = ''
    this.searchInputTarget.value = resetValueToMoveCursor
  }

  #resetInputValueIfPresent() {
    if (this.searchInputTarget.dataset.selectedValue) {
      this.searchInputTarget.value = this.searchInputTarget.dataset.selectedLabel
    } else {
      this.searchInputTarget.value = ''
    }
  }

  #showListboxContainer() {
    this.resultsContainerTarget.classList.remove('hidden')
    this.loadingContainerTarget.classList.add('hidden')
    this.listboxContainerTarget.classList.remove('hidden')
    this.listboxTarget.id = `${this.idValue}-listbox`
    this.searchInputTarget.setAttribute('aria-expanded', true)
    this.searchInputTarget.setAttribute(
      'aria-controls',
      `${this.idValue}-listbox`,
    )
  }

  #showLoadingContainer() {
    this.resultsContainerTarget.classList.remove('hidden')
    this.listboxContainerTarget.classList.add('hidden')
    this.loadingContainerTarget.classList.remove('hidden')
    this.searchInputTarget.removeAttribute('aria-controls')
    this.searchInputTarget.setAttribute('aria-expanded', false)
  }

  #storeSelectedOption({ label, value }) {
    this.searchInputTarget.value = label
    this.searchInputTarget.dataset.selectedLabel = label
    this.searchInputTarget.dataset.selectedValue = value
  }

  #styleSelectedOption() {
    const { selectedValue } = this.searchInputTarget.dataset

    if (selectedValue) {
      const selectedOption = this.optionTargets.find(
        (el) => el.dataset.selectValue === selectedValue,
      )

      if (selectedOption) {
        this.#applySelectedOptionStyling(selectedOption)
      }
    }
  }
}
